import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Stack,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import { useLazyQuery } from "@apollo/client";

import { GET_PRICE_MODEL } from "../../../graphql/queries/CompanyAdminContext";
import PriceModelPropertiesTable from "./PriceModelPropertiesTable";
import PricePropertyValuesEditor from "./PricePropertyValuesEditor";
import SimpleModalCompact from "../../common/ModalCompact/SimpleModalCompact";
import { FormattedMessage, useIntl } from "react-intl";

export default function PriceModelEditModal({
  open,
  onClose,
  pricingModelId,
  companyId,
  onSaved
}) {

  const intl = useIntl();
  const isNew = !pricingModelId || pricingModelId === 0;

  const [model, setModel] = useState({
    pricingModelId: 0,
    companyId,
    name: "",
    description: "",
    pricingProperties: []
  });

  const [loadPriceModel] = useLazyQuery(GET_PRICE_MODEL, {
    variables: { priceModelId: pricingModelId },
    onCompleted: (d) => {
      const pl = d?.companyAdminContext?.pricingModels?.pricingModel;
      if (pl) setModel(pl);
    },
  });

  useEffect(() => {
    if (!open) return;

    if (!isNew) {
      loadPriceModel();
    } else {
      setModel({
        pricingModelId: 0,
        companyId,
        name: "",
        description: "",
        pricingProperties: []
      });
    }
  }, [open, pricingModelId]);

  // -------------------------
  // BASIC FIELD UPDATE
  // -------------------------
  const updateField = (field, value) => {
    setModel(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // -------------------------
  // ADD PROPERTY
  // -------------------------
  const addProperty = () => {
    const newProperty = {
      id: 0,
      name: "",
      label: "",
      displayMode: "SELECT_ONE_DROPDOWN_REQUIRED",
      pricingPropertyValues: []
    };

    setModel(prev => ({
      ...prev,
      pricingProperties: [...prev.pricingProperties, newProperty]
    }));
  };

  // -------------------------
  // DELETE PROPERTY
  // -------------------------
  const deleteProperty = (property) => {
    setModel(prev => ({
      ...prev,
      pricingProperties: prev.pricingProperties.filter(p => p !== property)
    }));
  };

  // -------------------------
  // SORT UP
  // -------------------------
  const moveUp = (index) => {
    if (index === 0) return;

    setModel(prev => {
      const items = [...prev.pricingProperties];
      const [moved] = items.splice(index, 1);
      items.splice(index - 1, 0, moved);

      return {
        ...prev,
        pricingProperties: items
      };
    });
  };

  // -------------------------
  // SORT DOWN
  // -------------------------
  const moveDown = (index) => {
    setModel(prev => {
      const items = [...prev.pricingProperties];
      if (index >= items.length - 1) return prev;

      const [moved] = items.splice(index, 1);
      items.splice(index + 1, 0, moved);

      return {
        ...prev,
        pricingProperties: items
      };
    });
  };

  // -------------------------
  // UPDATE PROPERTY (stub)
  // -------------------------
  const updateProperty = (updatedProperty) => {
    setModel(prev => {
      const items = prev.pricingProperties.map(p =>
        p.id === updatedProperty.id ? updatedProperty : p
      );

      return {
        ...prev,
        pricingProperties: items
      };
    });

    setSelectedProperty(updatedProperty);
  };

  // -------------------------
  // SAVE (skiss kopplad till API)
  // -------------------------
  const save = async () => {

    const pricingPropertyIds = model.pricingProperties.map(p => p.id);

    // TODO: call updatePricingModel mutation
    // await updatePricingModel({ variables: { input: ... } });

    console.log("SAVE MODEL:", {
      ...model,
      pricingPropertyIds
    });

    onSaved?.();
    onClose();
  };

  const [selectedProperty, setSelectedProperty] = useState(null);
  const editProperty = (property) => {
    setSelectedProperty(property);
  };

  return (
    <SimpleModalCompact
      open={open}
      onClose={onClose}
      fullScreen
      title={
        isNew
          ? intl.formatMessage({ id: "admin.pricemodels.modal.new" })
          : intl.formatMessage({ id: "admin.pricemodels.modal.edit" })
      }
      footer={
        <>
          <Button onClick={onClose}>
            <FormattedMessage id="btn.cancel" />
          </Button>
          <Button variant="contained" onClick={save}>
            <FormattedMessage id="btn.save" />
          </Button>
        </>
      }
    >

      <Box sx={{ p: 1 }}>

        <Stack spacing={2} sx={{ mb: 4 }} direction="row">
          <TextField
            label={intl.formatMessage({ id: "common.name" })}
            value={model.name}
            onChange={(e) => updateField("name", e.target.value)}
            fullWidth
          />

          <TextField
            label={intl.formatMessage({ id: "common.description" })}
            value={model.description}
            onChange={(e) => updateField("description", e.target.value)}
            fullWidth
          />
        </Stack>

        <Button startIcon={<AddIcon />} onClick={addProperty} sx={{ mb: 1 }}>
          <FormattedMessage id="admin.pricemodels.rows.add" />
        </Button>

        <Box sx={{ overflowX: "auto", px: 2 }}>
          <PriceModelPropertiesTable
            properties={model.pricingProperties}
            onEditProperty={editProperty}
            onDeleteProperty={deleteProperty}
            onMoveUp={(_, index) => moveUp(index)}
            onMoveDown={(_, index) => moveDown(index)}
          />
        </Box>

        {selectedProperty && (
          <Box sx={{ mt: 3 }}>
            <PricePropertyValuesEditor
              values={selectedProperty.pricingPropertyValues || []}
              onChange={(updatedValues) => {
                updateProperty({
                  ...selectedProperty,
                  pricingPropertyValues: updatedValues
                });
              }}
            />
          </Box>
        )}

      </Box>
    </SimpleModalCompact>
  );
}